import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { MessageCircle, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackWhatsappClick, trackPhoneClick, trackFormSubmit, trackLeadThankYouView } from "@/lib/tracking";

const PACKAGE_LABELS = {
  "5_reels": "5 Reels (40€ / Reel)",
  "10_reels": "10 Reels (5% Rabatt)",
  "20_reels": "20 Reels (15% Rabatt)",
};

const Contact = forwardRef(function Contact({ settings }, ref) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    package: "5_reels",
    drone: false,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { whatsappUrl, name }
  const sectionRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setPackage: (pkgId) => {
      setForm((f) => ({ ...f, package: pkgId }));
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  }));

  const whatsappUrl = `https://wa.me/${settings.whatsapp_number}`;
  const phoneUrl = `tel:${settings.phone_number}`;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const buildWhatsappMessage = (data) => {
    const pkg = PACKAGE_LABELS[data.package] || data.package;
    const drone = data.drone ? "Ja" : "Nein";
    return encodeURIComponent(
      `Hallo MediaFly Production,\n\nich habe soeben das Formular ausgefüllt:\n\nName: ${data.name}\nTelefon: ${data.phone}\nE-Mail: ${data.email}\nPaket: ${pkg}\nDrohne: ${drone}\n\nNachricht:\n${data.message || "-"}\n`
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Bitte Name, Telefon und E-Mail ausfüllen.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        package: form.package,
        drone: form.drone,
        message: form.message,
      });
      trackFormSubmit(form.package, form.drone);
      const waUrl = `https://wa.me/${settings.whatsapp_number}?text=${buildWhatsappMessage(form)}`;
      setConfirmation({ whatsappUrl: waUrl, name: form.name, package: form.package, drone: form.drone });
      setForm({ name: "", phone: "", email: "", package: "5_reels", drone: false, message: "" });
      // Scroll confirmation into view on mobile
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error("Leider ging etwas schief. Bitte kontaktiere uns direkt per WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetConfirmation = () => setConfirmation(null);

  // Fire thank-you view event whenever confirmation appears
  useEffect(() => {
    if (confirmation) {
      trackLeadThankYouView(confirmation.package, confirmation.drone);
    }
  }, [confirmation]);

  return (
    <section
      ref={sectionRef}
      className="bg-white text-black"
      data-testid="contact-section"
      id="kontakt"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-24">
        {confirmation ? (
          <div
            className="max-w-3xl mx-auto text-center"
            data-testid="contact-confirmation"
          >
            <div className="inline-flex items-center justify-center h-20 w-20 md:h-24 md:w-24 bg-mfp-gold mb-6 md:mb-8">
              <CheckCircle2 className="h-10 w-10 md:h-12 md:w-12 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-mfp-gold font-bold mb-3">
              Fast geschafft
            </div>
            <h2
              className="font-display text-3xl md:text-5xl uppercase leading-[1.05] text-black"
              data-testid="confirmation-headline"
            >
              Danke{confirmation.name ? `, ${confirmation.name.split(" ")[0]}` : ""}!<br />
              Bestätige deine Anfrage<br />
              <span className="text-mfp-gold">in WhatsApp</span>.
            </h2>
            <p className="mt-5 text-black/70 text-base md:text-lg max-w-xl mx-auto">
              Wir haben deine Daten erhalten. Um sofort zu starten, öffne jetzt WhatsApp –
              deine Nachricht ist bereits vorformatiert. Ein Klick genügt.
            </p>

            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={confirmation.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsappClick("confirmation")}
                className="mfp-btn mfp-wa-btn group relative text-white text-sm md:text-base px-8 py-5 flex items-center justify-center gap-3 rounded-xl mfp-pulse overflow-hidden"
                data-testid="confirmation-whatsapp-cta"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <MessageCircle className="h-5 w-5 relative" />
                <span className="relative">Jetzt in WhatsApp bestätigen</span>
              </a>
              <button
                type="button"
                onClick={resetConfirmation}
                className="mfp-btn bg-transparent border-2 border-black/30 text-black text-sm md:text-base px-8 py-5 hover:bg-black hover:text-white transition-colors"
                data-testid="confirmation-new-request"
              >
                Neue Anfrage
              </button>
            </div>

            <div className="mt-10 pt-8 border-t border-black/10 text-xs md:text-sm text-black/50">
              Alternativ: <a href={phoneUrl} onClick={() => trackPhoneClick("confirmation")} className="underline hover:text-mfp-gold font-bold" data-testid="confirmation-phone-cta">Jetzt anrufen</a> &nbsp;·&nbsp;
              Antwort in der Regel innerhalb von 2 Stunden
            </div>
          </div>
        ) : (
          <>
        <div className="mb-10 md:mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-mfp-gold font-bold mb-3">
            Kontakt
          </div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] text-black">
            Lass uns deinen<br /><span className="text-mfp-gold">ersten Reel</span> planen.
          </h2>
          <p className="mt-4 text-black/60 text-base md:text-lg max-w-2xl">
            Wähle deinen Weg – WhatsApp ist am schnellsten. Antwort in der Regel innerhalb von 2 Stunden.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-8">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsappClick("contact")}
            className="mfp-btn mfp-wa-btn group relative text-white text-sm md:text-base px-6 py-5 flex items-center justify-center gap-3 rounded-xl overflow-hidden"
            data-testid="contact-whatsapp-cta"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <MessageCircle className="h-5 w-5 relative" />
            <span className="relative">Per WhatsApp schreiben</span>
          </a>
          <a
            href={phoneUrl}
            onClick={() => trackPhoneClick("contact")}
            className="mfp-btn bg-black text-white text-sm md:text-base px-6 py-5 flex items-center justify-center gap-3 hover:bg-mfp-gold transition-colors"
            data-testid="contact-phone-cta"
          >
            <Phone className="h-5 w-5" />
            Jetzt anrufen
          </a>
        </div>

        <div className="border-t border-black/10 pt-8 md:pt-12">
          <div className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-6">
            Oder per Formular
          </div>
          <form
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8"
            data-testid="contact-form"
          >
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-widest text-black/50 font-bold">Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                className="bg-transparent border-b-2 border-black/20 py-3 px-0 focus:outline-none focus:border-mfp-gold transition-colors font-medium"
                data-testid="form-name"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-widest text-black/50 font-bold">Telefon *</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={onChange}
                required
                className="bg-transparent border-b-2 border-black/20 py-3 px-0 focus:outline-none focus:border-mfp-gold transition-colors font-medium"
                data-testid="form-phone"
              />
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-black/50 font-bold">E-Mail *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                required
                className="bg-transparent border-b-2 border-black/20 py-3 px-0 focus:outline-none focus:border-mfp-gold transition-colors font-medium"
                data-testid="form-email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-widest text-black/50 font-bold">Paket</label>
              <select
                name="package"
                value={form.package}
                onChange={onChange}
                className="bg-white border-2 border-black/20 py-3 px-4 focus:outline-none focus:border-mfp-gold font-medium"
                data-testid="form-package"
              >
                <option value="5_reels">5 Reels – Mindestabnahme</option>
                <option value="10_reels">10 Reels – 5% Rabatt</option>
                <option value="20_reels">20 Reels – 15% Rabatt</option>
              </select>
            </div>
            <div className="flex items-end gap-3">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="drone"
                  checked={form.drone}
                  onChange={onChange}
                  className="h-5 w-5 accent-mfp-gold"
                  data-testid="form-drone"
                />
                <span className="text-sm font-bold uppercase tracking-widest">
                  Drohne (+30€ / Einsatz)
                </span>
              </label>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-black/50 font-bold">Nachricht</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={4}
                className="bg-transparent border-b-2 border-black/20 py-3 px-0 focus:outline-none focus:border-mfp-gold transition-colors font-medium resize-none"
                placeholder="Worum geht's? (optional)"
                data-testid="form-message"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="mfp-btn w-full bg-black text-white text-sm md:text-base px-6 py-5 flex items-center justify-center gap-3 hover:bg-mfp-gold transition-colors disabled:opacity-60"
                data-testid="form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Wird gesendet…
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Anfrage senden & zu WhatsApp
                  </>
                )}
              </button>
              <p className="mt-4 text-xs text-black/50">
                Mit dem Absenden stimmst du unserer{" "}
                <a href="/datenschutz" className="underline hover:text-mfp-gold" data-testid="form-privacy-link">
                  Datenschutzerklärung
                </a>{" "}
                zu.
              </p>
            </div>
          </form>
        </div>
          </>
        )}
      </div>
    </section>
  );
});

export default Contact;
